import {
  T,
  formatCents,
  ingredientGroups,
  ingredientPriceCents,
  sizeCmFromName,
  tt,
  type BuilderIngredient,
} from '@agos/shared'
import { cn } from '@agos/ui'
import { Pizza, X } from 'lucide-react'
import { useState } from 'react'

import {
  cycleExtra,
  extrasCents,
  extrasText,
  parseExtras,
  type ChosenExtras,
} from './pizza-extras'

/**
 * Toppings on a pizza off the menu — `FRONTEND.md` §9.41, ported from legacy
 * `pizza-extras-modal.tsx`.
 *
 * Grouped by §9.25's rule rather than legacy's hard-coded `["Syr","Mäso","Zelenina"]`: that drops the
 * *Základ* rows — a pizza that already comes with a tomato base cannot take a second one as an extra —
 * and shows a category a franchise has added, which legacy's list would hide.
 */
export function PizzaExtrasDialog({
  productName,
  unitPrice,
  ingredients,
  initialExtras,
  onClose,
  onConfirm,
}: {
  productName: string
  unitPrice: number | string
  ingredients: readonly BuilderIngredient[]
  initialExtras?: string
  onClose: () => void
  /**
   * §10A.80 — the chosen ids travel with the text and the price. The server prices them; the price
   * here is what the screen quotes, and the two agree because both read `ingredientForSize`.
   */
  onConfirm: (
    extras: string,
    extrasPriceCents: number,
    chosen: ReadonlyArray<{ ingredientId: number; quantity: number }>,
  ) => void
}) {
  // The size is read off the product's name, which is where it lives — *Margherita 50 cm*. Legacy
  // does the same and falls back to 32, the size a pizza is when nobody said otherwise.
  const sizeCm = sizeCmFromName(productName) ?? 32
  // §9.93 — an `Okraj` row is an ordinary topping now, so `ingredientGroups` returns it with the
  // rest and there is nothing separate to draw or to price.
  const groups = ingredientGroups(ingredients)
  const offered = groups.flatMap((group) => group.ingredients)
  const [chosen, setChosen] = useState<ChosenExtras>(() => parseExtras(initialExtras, offered))

  const surcharge = extrasCents(chosen, offered, sizeCm)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-pizza-extras>
      <button
        type="button"
        className="absolute inset-0 bg-black/60"
        aria-label={tt('Zavrieť')}
        onClick={onClose}
      />
      <section className="relative w-full max-w-md max-h-[85vh] flex flex-col rounded-2xl bg-background border border-border overflow-hidden">
        <header className="px-5 pt-4 pb-3 border-b border-border shrink-0 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              <Pizza className="w-4 h-4 text-primary" />
              <T>Prísady navyše</T>
            </p>
            <h2 className="text-base font-bold leading-tight truncate">{productName}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {sizeCm} cm · {formatCents(surcharge)}
            </p>
          </div>
          <button
            type="button"
            aria-label={tt('Zavrieť')}
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-muted shrink-0"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {groups.map((group) => (
            <div key={group.category}>
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
                <T>{group.category}</T>
              </p>
              <div className="grid grid-cols-2 gap-1.5">
                {group.ingredients.map((ingredient) => {
                  const qty = chosen.get(ingredient.id)
                  return (
                    <button
                      key={ingredient.id}
                      type="button"
                      data-extra={ingredient.id}
                      data-extra-qty={qty ?? 0}
                      // Tapped again for a double portion, again to remove it — legacy's cycle.
                      onClick={() => setChosen((current) => cycleExtra(current, ingredient.id))}
                      className={cn(
                        'flex items-center justify-between gap-2 rounded-xl border p-2 text-xs text-left transition-colors',
                        qty === undefined
                          ? 'border-border bg-card hover:border-primary/50'
                          : qty === 1
                            ? 'border-primary bg-primary/10 font-semibold'
                            : 'border-orange-500 bg-orange-500/10 font-semibold',
                      )}
                    >
                      <span className="truncate">{ingredient.name}</span>
                      <span className="shrink-0 tabular-nums text-[10px] text-muted-foreground">
                        {qty === 2 ? '2× ' : ''}
                        {formatCents(ingredientPriceCents(ingredient, sizeCm) * (qty ?? 1))}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
        </div>

        <footer className="p-4 border-t border-border shrink-0 space-y-2">
          <p className="text-xs text-muted-foreground" data-extras-summary>
            {extrasText(chosen, offered) === '' ? (
              <T>Bez prísad navyše</T>
            ) : (
              extrasText(chosen, offered)
            )}
          </p>
          <button
            type="button"
            data-extras-save
            onClick={() =>
              onConfirm(
                extrasText(chosen, offered),
                surcharge,
                [...chosen].map(([ingredientId, quantity]) => ({ ingredientId, quantity })),
              )
            }
            className="w-full min-h-[48px] rounded-xl bg-primary text-primary-foreground font-bold hover:opacity-90 transition-opacity"
          >
            <T>Uložiť</T> · {formatCents(Math.round(Number(unitPrice) * 100) + surcharge)}
          </button>
        </footer>
      </section>
    </div>
  )
}
